import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { PurchaseItem } from '../types/purchase';
import {
  computePurchaseUnitPriceFixes,
  PurchaseUnitPriceFixResult,
} from '../utils/purchaseUnitPriceFix';
import { createWarehousePriceResolver } from '../utils/warehousePriceResolver';
import { warehouseGoodsCatalogService } from './warehouseGoodsCatalogService';
import { warehouseService } from './warehouseService';

const COLLECTION_NAME = 'calendar-purchases';

const toDateValue = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value?.toDate) return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const normalizeNumber = (value: number | string | undefined): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
};

const normalizeNameForMatch = (value?: string): string => {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const stripMonthToken = (value?: string): string => {
  return (value || '')
    // Remove explicit month markers: t1..t12, th1..th12, thang 1..12.
    .replace(/\b(t|th|thang)\s*([1-9]|1[0-2])\b/gi, ' ')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const cleanupDisplayName = (value?: string): string => {
  return (value || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const levenshteinDistance = (a: string, b: string): number => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const matrix: number[][] = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));

  for (let i = 0; i <= a.length; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[a.length][b.length];
};

const similarityScore = (a: string, b: string): number => {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const maxLen = Math.max(a.length, b.length);
  if (!maxLen) return 1;
  return 1 - levenshteinDistance(a, b) / maxLen;
};

const isSimilarIngredientName = (a: string, b: string, threshold: number): boolean => {
  if (!a || !b) return false;
  if (a === b) return true;

  const minLen = Math.min(a.length, b.length);
  const maxLen = Math.max(a.length, b.length);
  if (minLen / maxLen < 0.6) return false;

  if ((a.startsWith(b) || b.startsWith(a)) && minLen / maxLen >= threshold) {
    return true;
  }

  return similarityScore(a, b) >= threshold;
};

export interface NormalizePurchaseItemNamesResult {
  totalDocs: number;
  uniqueNames: number;
  canonicalNames: number;
  renamedDocs: number;
  changedPairs: Array<{ from: string; to: string; count: number }>;
}

export interface NormalizePurchaseUnitsResult {
  totalDocs: number;
  normalizedDocs: number;
  convertedToGramDocs: number;
  convertedToMlDocs: number;
  changedPairs: Array<{ from: string; to: string; count: number }>;
}

export type NormalizePurchaseUnitPricesResult = PurchaseUnitPriceFixResult;

const roundNumber = (value: number, precision = 6): number => {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

const normalizeUnitKey = (value?: string): string => {
  return normalizeNameForMatch(value)
    .replace(/\s+/g, '')
    .replace(/\./g, '');
};


const normalizePurchaseUnitValue = (quantityRaw: any, unitRaw: any, unitPriceRaw: any) => {
  const quantity = normalizeNumber(quantityRaw);
  const unitPrice = normalizeNumber(unitPriceRaw);
  const inputUnit = String(unitRaw || '').trim();
  const unitKey = normalizeUnitKey(inputUnit);

  const kgUnits = new Set(['kg', 'kilo', 'kilogram']);
  const gUnits = new Set(['g', 'gram', 'gam']);
  const literUnits = new Set(['l', 'lit', 'liter', 'litre']);
  const mlUnits = new Set(['ml', 'mililit', 'milliliter', 'millilitre']);

  let normalizedQuantity = quantity;
  let normalizedUnitPrice = unitPrice;
  let normalizedUnit = inputUnit;
  /** Đồng bộ kho: mọi khối lượng/thể tích quy về gam (l ≈ kg → ×1000 g; ml ≈ g). */
  let convertedTo: 'g' | null = null;
  let changed = false;

  if (kgUnits.has(unitKey)) {
    normalizedQuantity = quantity * 1000;
    normalizedUnitPrice = unitPrice / 1000;
    normalizedUnit = 'g';
    convertedTo = 'g';
    changed = true;
  } else if (gUnits.has(unitKey)) {
    normalizedUnit = 'g';
    changed = normalizedUnit !== inputUnit;
  } else if (literUnits.has(unitKey)) {
    normalizedQuantity = quantity * 1000;
    normalizedUnitPrice = unitPrice / 1000;
    normalizedUnit = 'g';
    convertedTo = 'g';
    changed = true;
  } else if (mlUnits.has(unitKey)) {
    normalizedUnit = 'g';
    convertedTo = 'g';
    changed = normalizeUnitKey(inputUnit) !== 'g';
  }

  const finalQuantity = roundNumber(normalizedQuantity);
  const finalUnitPrice = roundNumber(normalizedUnitPrice);
  const finalTotal = roundNumber(finalQuantity * finalUnitPrice);

  if (!changed) {
    return {
      changed: false,
      inputUnit,
      normalizedUnit,
      quantity: finalQuantity,
      unitPrice: finalUnitPrice,
      total: finalTotal,
      convertedTo,
    };
  }

  return {
    changed: true,
    inputUnit,
    normalizedUnit,
    quantity: finalQuantity,
    unitPrice: finalUnitPrice,
    total: finalTotal,
    convertedTo,
  };
};

const normalizePurchaseItem = (raw: any): PurchaseItem => {
  const quantity = normalizeNumber(raw.quantity);
  const unitPrice = normalizeNumber(raw.unitPrice);
  const total = normalizeNumber(raw.total) || quantity * unitPrice;

  return {
    id: raw.id,
    code: raw.code || '',
    category: raw.category || '',
    supplierSource: String(raw.supplierSource || ''),
    supplierId: String(raw.supplierId || ''),
    date: toDateValue(raw.date),
    itemName: String(raw.itemName || ''),
    description: String(raw.description || ''),
    quantity,
    unit: String(raw.unit || ''),
    unitPrice,
    total,
    createdBy: String(raw.createdBy || ''),
    createdAt: toDateValue(raw.createdAt),
    storeLocationId: raw.storeLocationId || '',
    fromWarehouse: raw.fromWarehouse === true,
    warehouseCoffeeId: raw.warehouseCoffeeId || undefined,
    warehouseCoffeeName: raw.warehouseCoffeeName || undefined,
    isTransferOut: raw.isTransferOut === true,
    isTransferIn: raw.isTransferIn === true,
    transferToStoreId: raw.transferToStoreId || undefined,
    transferFromStoreId: raw.transferFromStoreId || undefined,
    linkedTransferId: raw.linkedTransferId || undefined,
  };
};

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const startOfWeekMonday = (date: Date) => {
  const d = startOfDay(date);
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  return d;
};

const endOfWeekSunday = (date: Date) => {
  const d = startOfWeekMonday(date);
  d.setDate(d.getDate() + 6);
  return endOfDay(d);
};

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
const endOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);

const startOfYear = (date: Date) => new Date(date.getFullYear(), 0, 1, 0, 0, 0, 0);
const endOfYear = (date: Date) => new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999);

const getByDateRange = async (startDate: Date, endDate: Date): Promise<PurchaseItem[]> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('date', '>=', Timestamp.fromDate(startDate)),
    where('date', '<=', Timestamp.fromDate(endDate)),
    orderBy('date', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((item) => normalizePurchaseItem({ id: item.id, ...item.data() }));
};

export const purchaseService = {
  async addPurchaseItem(data: Omit<PurchaseItem, 'id' | 'createdAt' | 'total'> & { total?: number }): Promise<PurchaseItem> {
    const quantity = normalizeNumber(data.quantity);
    const unitPrice = normalizeNumber(data.unitPrice);
    const total = quantity * unitPrice;

    const payload = {
      ...data,
      date: Timestamp.fromDate(toDateValue(data.date)),
      quantity,
      unitPrice,
      total,
      createdAt: Timestamp.fromDate(new Date()),
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), payload);
    return normalizePurchaseItem({ id: docRef.id, ...payload });
  },

  async addStoreTransfer(data: {
    date: Date;
    itemName: string;
    unit: string;
    quantity: number;
    unitPrice: number;
    fromStoreId: string;
    fromStoreName: string;
    toStoreId: string;
    toStoreName: string;
    notes?: string;
    createdBy?: string;
  }): Promise<{ outRecord: PurchaseItem; inRecord: PurchaseItem }> {
    const qty = normalizeNumber(data.quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      throw new Error('Quantity must be > 0');
    }

    const unitPrice = normalizeNumber(data.unitPrice);
    const safeUnit = String(data.unit || '').trim();
    const safeItemName = String(data.itemName || '').trim();
    const fromStoreId = String(data.fromStoreId || '').trim();
    const toStoreId = String(data.toStoreId || '').trim();
    if (!safeItemName || !safeUnit || !fromStoreId || !toStoreId) {
      throw new Error('Missing required fields');
    }
    if (fromStoreId === toStoreId) {
      throw new Error('fromStoreId must be different from toStoreId');
    }

    const now = Timestamp.fromDate(new Date());
    const dateTs = Timestamp.fromDate(toDateValue(data.date));

    const outRef = doc(collection(db, COLLECTION_NAME));
    const inRef = doc(collection(db, COLLECTION_NAME));

    // Firestore payload uses Timestamp; PurchaseItem uses Date (normalizePurchaseItem will convert).
    const outPayload = {
      code: '',
      category: 'Chuyển nội bộ',
      supplierSource: '',
      date: dateTs,
      itemName: safeItemName,
      description: (data.notes || `Chuyển sang ${data.toStoreName || toStoreId}`).trim(),
      quantity: -Math.abs(qty),
      unit: safeUnit,
      unitPrice,
      total: 0,
      createdBy: String(data.createdBy || ''),
      createdAt: now,
      updatedAt: now,
      storeLocationId: fromStoreId,
      isTransferOut: true,
      isTransferIn: false,
      transferToStoreId: toStoreId,
      transferFromStoreId: undefined,
      linkedTransferId: inRef.id,
    };

    const inPayload = {
      code: '',
      category: 'Chuyển nội bộ',
      supplierSource: '',
      date: dateTs,
      itemName: safeItemName,
      description: (data.notes || `Nhận từ ${data.fromStoreName || fromStoreId}`).trim(),
      quantity: Math.abs(qty),
      unit: safeUnit,
      unitPrice,
      total: qty * unitPrice,
      createdBy: String(data.createdBy || ''),
      createdAt: now,
      updatedAt: now,
      storeLocationId: toStoreId,
      isTransferOut: false,
      isTransferIn: true,
      transferToStoreId: undefined,
      transferFromStoreId: fromStoreId,
      linkedTransferId: outRef.id,
    };

    const batch = writeBatch(db);
    batch.set(outRef, outPayload as any);
    batch.set(inRef, inPayload as any);
    await batch.commit();

    return {
      outRecord: normalizePurchaseItem({ id: outRef.id, ...outPayload }),
      inRecord: normalizePurchaseItem({ id: inRef.id, ...inPayload }),
    };
  },

  async deleteStoreTransfer(id: string): Promise<void> {
    const primaryId = String(id || '').trim();
    if (!primaryId) return;

    const primaryRef = doc(db, COLLECTION_NAME, primaryId);
    const primarySnap = await getDoc(primaryRef);
    if (!primarySnap.exists()) return;

    const primary = normalizePurchaseItem({ id: primarySnap.id, ...primarySnap.data() });
    const linkedId = String(primary.linkedTransferId || '').trim();

    const batch = writeBatch(db);
    batch.delete(primaryRef);
    if (linkedId) {
      batch.delete(doc(db, COLLECTION_NAME, linkedId));
    }
    await batch.commit();
  },

  async updatePurchaseItem(id: string, data: Partial<PurchaseItem>): Promise<void> {
    const docRef = doc(db, COLLECTION_NAME, id);
    const quantity = normalizeNumber(data.quantity);
    const unitPrice = normalizeNumber(data.unitPrice);

    const payload: Record<string, any> = {
      ...data,
      updatedAt: Timestamp.fromDate(new Date()),
    };

    if (data.date) {
      payload.date = Timestamp.fromDate(toDateValue(data.date));
    }

    if (data.quantity !== undefined || data.unitPrice !== undefined) {
      const safeQuantity = data.quantity !== undefined ? quantity : undefined;
      const safeUnitPrice = data.unitPrice !== undefined ? unitPrice : undefined;
      payload.quantity = safeQuantity ?? data.quantity;
      payload.unitPrice = safeUnitPrice ?? data.unitPrice;
      const mergedQuantity = safeQuantity ?? 0;
      const mergedUnitPrice = safeUnitPrice ?? 0;
      payload.total = mergedQuantity * mergedUnitPrice;
    }

    await updateDoc(docRef, payload);
  },

  async deletePurchaseItem(id: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION_NAME, id));
  },

  async getAllPurchaseItems(): Promise<PurchaseItem[]> {
    const q = query(collection(db, COLLECTION_NAME), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => normalizePurchaseItem({ id: item.id, ...item.data() }));
  },

  async getPurchaseItemsByDate(date: Date): Promise<PurchaseItem[]> {
    return getByDateRange(startOfDay(date), endOfDay(date));
  },

  async getPurchaseItemsByWeek(date: Date): Promise<PurchaseItem[]> {
    return getByDateRange(startOfWeekMonday(date), endOfWeekSunday(date));
  },

  async getPurchaseItemsByMonth(date: Date): Promise<PurchaseItem[]> {
    return getByDateRange(startOfMonth(date), endOfMonth(date));
  },

  async getPurchaseItemsByYear(date: Date): Promise<PurchaseItem[]> {
    return getByDateRange(startOfYear(date), endOfYear(date));
  },

  async getPurchaseItemById(id: string): Promise<PurchaseItem | null> {
    const ref = doc(db, COLLECTION_NAME, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      return null;
    }

    return normalizePurchaseItem({ id: snap.id, ...snap.data() });
  },

  async normalizePurchaseItemNames(similarityThreshold = 0.9): Promise<NormalizePurchaseItemNamesResult> {
    const safeThreshold = Math.max(0.8, Math.min(similarityThreshold, 1));
    const q = query(collection(db, COLLECTION_NAME), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);

    const rawDocs = snapshot.docs.map((item) => ({
      id: item.id,
      ref: doc(db, COLLECTION_NAME, item.id),
      itemName: String(item.data().itemName || '').trim(),
    }));

    const docsWithName = rawDocs
      .filter((item) => item.itemName)
      .map((item) => {
        const withoutMonth = stripMonthToken(item.itemName);
        const cleaned = cleanupDisplayName(withoutMonth || item.itemName);
        return {
          ...item,
          normalizedKey: normalizeNameForMatch(cleaned),
          canonicalCandidate: cleaned || item.itemName,
        };
      })
      .filter((item) => item.normalizedKey);

    const variantMap = new Map<string, { key: string; displayName: string; count: number }>();
    docsWithName.forEach((item) => {
      const key = item.normalizedKey;
      if (!key) return;

      const existing = variantMap.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        variantMap.set(key, {
          key,
          displayName: item.canonicalCandidate,
          count: 1,
        });
      }
    });

    const variants = Array.from(variantMap.values()).sort((a, b) => b.count - a.count);
    const used = new Set<string>();
    const canonicalByKey = new Map<string, string>();

    variants.forEach((seed) => {
      if (used.has(seed.key)) return;

      const cluster = variants.filter((candidate) => {
        if (used.has(candidate.key)) return false;
        return isSimilarIngredientName(seed.key, candidate.key, safeThreshold);
      });

      const canonical = cluster
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return a.displayName.localeCompare(b.displayName, 'vi');
        })[0]
        .displayName;

      cluster.forEach((member) => {
        used.add(member.key);
        canonicalByKey.set(member.key, canonical);
      });
    });

    const changedDocs = docsWithName
      .map((item) => {
        const key = item.normalizedKey;
        const canonical = canonicalByKey.get(key) || item.itemName;
        return {
          ...item,
          canonical,
          changed: item.itemName !== canonical,
        };
      })
      .filter((item) => item.changed);

    const pairCounter = new Map<string, number>();
    changedDocs.forEach((item) => {
      const pairKey = `${item.itemName}__=>__${item.canonical}`;
      pairCounter.set(pairKey, (pairCounter.get(pairKey) || 0) + 1);
    });

    for (let i = 0; i < changedDocs.length; i += 450) {
      const chunk = changedDocs.slice(i, i + 450);
      const batch = writeBatch(db);
      chunk.forEach((item) => {
        batch.update(item.ref, {
          itemName: item.canonical,
          updatedAt: Timestamp.fromDate(new Date()),
        });
      });
      await batch.commit();
    }

    const changedPairs = Array.from(pairCounter.entries())
      .map(([key, count]) => {
        const [from, to] = key.split('__=>__');
        return { from, to, count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);

    return {
      totalDocs: rawDocs.length,
      uniqueNames: variants.length,
      canonicalNames: new Set(canonicalByKey.values()).size,
      renamedDocs: changedDocs.length,
      changedPairs,
    };
  },

  async normalizePurchaseUnits(): Promise<NormalizePurchaseUnitsResult> {
    const q = query(collection(db, COLLECTION_NAME), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);

    const rawDocs = snapshot.docs.map((item) => {
      const data = item.data();
      return {
        id: item.id,
        ref: doc(db, COLLECTION_NAME, item.id),
        quantity: data.quantity,
        unit: data.unit,
        unitPrice: data.unitPrice,
      };
    });

    const docsToUpdate = rawDocs
      .map((item) => {
        const normalized = normalizePurchaseUnitValue(item.quantity, item.unit, item.unitPrice);
        return {
          ...item,
          normalized,
        };
      })
      .filter((item) => item.normalized.changed);

    for (let i = 0; i < docsToUpdate.length; i += 450) {
      const chunk = docsToUpdate.slice(i, i + 450);
      const batch = writeBatch(db);
      chunk.forEach((item) => {
        batch.update(item.ref, {
          quantity: item.normalized.quantity,
          unit: item.normalized.normalizedUnit,
          unitPrice: item.normalized.unitPrice,
          total: item.normalized.total,
          updatedAt: Timestamp.fromDate(new Date()),
        });
      });
      await batch.commit();
    }

    const pairCounter = new Map<string, number>();
    let convertedToGramDocs = 0;

    docsToUpdate.forEach((item) => {
      const from = item.normalized.inputUnit || '(trống)';
      const to = item.normalized.normalizedUnit || '(trống)';
      const key = `${from}__=>__${to}`;
      pairCounter.set(key, (pairCounter.get(key) || 0) + 1);

      if (item.normalized.convertedTo === 'g') convertedToGramDocs += 1;
    });

    const changedPairs = Array.from(pairCounter.entries())
      .map(([key, count]) => {
        const [from, to] = key.split('__=>__');
        return { from, to, count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);

    return {
      totalDocs: rawDocs.length,
      normalizedDocs: docsToUpdate.length,
      convertedToGramDocs,
      /** Giữ trường tương thích API cũ; mọi quy đổi thể tích giờ gộp vào gam. */
      convertedToMlDocs: 0,
      changedPairs,
    };
  },

  async normalizePurchaseUnitPrices(maxBadUnitPrice = 1): Promise<NormalizePurchaseUnitPricesResult> {
    const q = query(collection(db, COLLECTION_NAME), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);

    const items = snapshot.docs.map((item) => normalizePurchaseItem({ id: item.id, ...item.data() }));

    const [warehouseEntries, goodsCatalog] = await Promise.all([
      warehouseService.getAllEntries(),
      warehouseGoodsCatalogService.getAll(),
    ]);

    const resolveExternalPrice = createWarehousePriceResolver(goodsCatalog, warehouseEntries);

    const result = computePurchaseUnitPriceFixes(items, {
      maxBadUnitPrice,
      resolveExternalPrice,
    });

    for (let i = 0; i < result.updates.length; i += 450) {
      const chunk = result.updates.slice(i, i + 450);
      const batch = writeBatch(db);
      chunk.forEach((update) => {
        const ref = doc(db, COLLECTION_NAME, update.id);
        batch.update(ref, {
          unitPrice: update.unitPrice,
          total: update.total,
          updatedAt: Timestamp.fromDate(new Date()),
        });
      });
      await batch.commit();
    }

    return result;
  },
};
